import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, BookOpen, Feather, ArrowRight } from "lucide-react";

const items = [
  { icon: BookOpen, name: "L'Aventure", price: "19,90 €", accent: "#385E3A" },
  { icon: Sparkles, name: "Le Jeu", price: "9,90 €", accent: "#2E5B7F" },
  { icon: Feather, name: "1 carnet PDF au choix", price: "9,90 €", accent: "#A63B36" },
];

export const PackHighlight = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [carnets, setCarnets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/carnets"), api.get("/foundation").catch(() => ({ data: null }))]).then(([r, f]) => {
      const foundationId = f.data?.id;
      setCarnets((r.data || []).filter((c) => c.visible && c.active_sale && (!foundationId || c.book_id !== foundationId)));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const startCheckout = async (carnetId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { product: "pack", carnet_id: carnetId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 400) toast.success("Pack déjà débloqué");
      else toast.error("Paiement impossible");
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    const pending = sessionStorage.getItem("tao_pack_pending");
    if (pending) { sessionStorage.removeItem("tao_pack_pending"); startCheckout(pending); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const buy = () => {
    if (!selected) { toast.error("Choisissez d'abord votre carnet PDF"); return; }
    sessionStorage.setItem("tao_unlock_book", "jeu-ile-de-labondance");
    if (!authed) {
      sessionStorage.setItem("tao_pack_pending", selected);
      navigate(`/connexion?next=${window.location.pathname}`);
      return;
    }
    startCheckout(selected);
  };

  const hasCarnets = carnets.length > 0;

  return (
    <section data-testid="pack-section" className="bg-[#1F2A24] text-[#F6F2E9]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 md:py-24">
        <div className="rounded-3xl border border-[#C9A24B]/40 bg-gradient-to-br from-[#C9A24B]/12 to-transparent p-8 md:p-12">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-4 py-1.5 font-ui text-xs tracking-[0.2em] uppercase font-semibold">
              <Sparkles size={14} /> L'essentiel
            </span>
            <h2 className="font-serif text-3xl md:text-5xl mt-5">Le Pack 3 Univers</h2>
            <p className="font-serif italic text-lg text-[#F6F2E9]/80 mt-3">Aventure + Jeu + 1 carnet PDF au choix</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-10" data-testid="pack-items">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.name} className="rounded-2xl border border-[#C9A24B]/20 bg-white/5 p-6 text-center">
                  <span className="w-11 h-11 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${it.accent}33`, color: "#C9A24B" }}>
                    <Icon size={20} />
                  </span>
                  <p className="font-serif text-lg mt-4">{it.name}</p>
                  <p className="font-sans text-sm text-[#F6F2E9]/50 mt-1 line-through">{it.price}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-5 mt-10">
            <div className="text-center">
              <p className="font-sans text-sm text-[#F6F2E9]/50 line-through">Valeur individuelle 39,70 €</p>
              <p className="font-serif text-5xl text-[#C9A24B] leading-none mt-1" data-testid="pack-price">29,90 €</p>
            </div>
            <span className="rounded-full bg-[#A63B36] text-white px-4 py-1.5 font-ui text-sm tracking-wide">Vous économisez 9,80 €</span>
          </div>

          <div className="mt-10 max-w-md mx-auto">
            <label htmlFor="pack-carnet" className="block font-ui text-sm text-[#F6F2E9]/80 mb-2">Choisissez votre carnet PDF</label>
            <select
              id="pack-carnet"
              data-testid="pack-carnet-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={!hasCarnets}
              className="w-full rounded-full bg-white/10 border border-[#C9A24B]/40 text-[#F6F2E9] px-5 py-3 font-ui text-sm focus:outline-none focus:border-[#C9A24B] disabled:opacity-50"
            >
              <option value="" className="text-[#1F2A24]">{!loaded ? "Chargement…" : hasCarnets ? "— Sélectionnez un carnet —" : "Carnets bientôt disponibles"}</option>
              {carnets.map((c) => (
                <option key={c.id} value={c.id} className="text-[#1F2A24]">
                  {`Carnet ${c.number ? c.number + " — " : ""}${c.title || c.name || ""}`}
                </option>
              ))}
            </select>
            <button
              data-testid="pack-buy-btn"
              onClick={buy}
              disabled={busy || !hasCarnets}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#b8923f] transition-colors disabled:opacity-50"
            >
              {busy ? "Redirection…" : "Obtenir le Pack — 29,90 €"} <ArrowRight size={18} />
            </button>
            <p className="text-center font-sans text-xs text-[#F6F2E9]/50 mt-3">
              Accès immédiat à l'Aventure, au Jeu et à votre carnet PDF après paiement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
